// Scroll story — pinned, scroll-driven "cinematic filmstrip" driver for the `scroll-story` section.
//
// PINNED mode (default, when motion is allowed and there are 2+ chapters):
//   The section has a tall outer wrapper (`[data-ss-pin]`, height = chapters × 100svh) with a sticky
//   inner stage (`[data-ss-stage]`, 100svh) that LOCKS on screen while the visitor scrolls through the
//   wrapper. A passive, rAF-throttled scroll handler maps scroll progress within the pinned range to a
//   continuous scene position `p` (0…N-1). For each chapter i it writes two custom properties —
//   `--ss-x` (= i − p, its signed distance from centre) and `--ss-abs` (|i − p|) — and CSS turns those
//   into a horizontal filmstrip slide: the centred scene fills the frame, the next slides in from the
//   right and the previous slides out to the left, with a depth scale + dim on the off-centre scenes
//   and a faster, fading parallax on the captions. The page still scrolls normally — nothing is
//   hijacked/preventDefault-ed; the section just pins and animates on progress. Each 0→1 hand-off is
//   smoothstep-eased so scenes dwell then slide (cinematic) while staying monotonic with scroll.
//
// STACKED mode (reduced motion, or <2 chapters): no pin, no scroll-linked transforms. Chapters are
//   simple full-screen panels stacked in normal flow; a centre-line IntersectionObserver tracks the
//   active one. This is also exactly the no-JS/no-CSS-class fallback.
//
// Both modes: active chapter lights its progress dot, announces on an aria-live line, and plays/pauses
// its backdrop video (WCAG 2.2.2 toggle, never autoplays under reduced motion). A second observer
// toggles `scroll-story--in-view` so the fixed progress rail only shows while the section is on screen.
// Reaps on section unload so observers/listeners don't leak across editor re-renders.

import { mountInstances } from '@theme/ca-section-instances';

(() => {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ScrollStory {
    constructor(root) {
      this.root = root;
      this.chapters = Array.from(root.querySelectorAll('[data-ss-chapter]'));
      this.dots = Array.from(root.querySelectorAll('[data-ss-dot]'));
      this.status = root.querySelector('[data-ss-status]');

      // id → { chapter, index, dot, video, toggle }. Backdrop media (video/toggle) now lives inside
      // each chapter, so it's read from the chapter — not a separate stage layer.
      this.byId = new Map();
      this.chapters.forEach((chapter, index) => {
        const id = chapter.dataset.ssId;
        const dot = this.dots.find((d) => d.dataset.ssId === id) || null;

        // Enrich the (generic) dot label with this chapter's heading, read from the DOM — the section
        // can't read block.settings.heading, but the rendered card can.
        if (dot) {
          const heading = chapter.querySelector('.scroll-story__heading');
          const title = heading && heading.textContent ? heading.textContent.trim() : '';
          if (title) {
            // Localized template from the root (data-i18n-chapter), e.g. "Go to chapter {index} of 3".
            const tmpl = root.dataset.i18nChapter || 'Go to chapter %index% of ' + this.chapters.length;
            const label = tmpl.replace('%index%', index + 1);
            dot.setAttribute('aria-label', `${label}: ${title}`);
          }
        }

        this.byId.set(id, {
          chapter,
          index,
          dot,
          video: chapter.querySelector('video.scroll-story__video'),
          toggle: chapter.querySelector('[data-ss-video-toggle]'),
        });
      });

      this.pin = root.querySelector('[data-ss-pin]');
      this.stage = root.querySelector('[data-ss-stage]');

      this.activeId = null;
      this.visible = new Set(); // ids currently crossing the center line (stacked mode)
      this.userPaused = new Set(); // ids whose video the visitor paused via the toggle
      this.mode = null; // 'pinned' | 'stacked'
      this._ticking = false;
      this._rafId = 0;

      this.onIntersect = this.onIntersect.bind(this);
      this.onSectionInView = this.onSectionInView.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.update = this.update.bind(this);

      this.bindDots();
      this.bindToggles();

      // React to a mid-session reduced-motion change: switch pinned⇄stacked and stop/resume video.
      if (REDUCE.addEventListener) REDUCE.addEventListener('change', this.onMotionChange);
      else if (REDUCE.addListener) REDUCE.addListener(this.onMotionChange); // older Safari

      // Centre-line observer (a 1px band at 50% height) — drives the active chapter in STACKED mode.
      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0,
      });

      // Show the fixed progress rail only while the section is on screen.
      this.inViewObserver = new IntersectionObserver(this.onSectionInView, { threshold: 0 });
      this.inViewObserver.observe(root);

      document.addEventListener('shopify:block:select', this.onBlockSelect);

      // Gate the card-lift + progress rail CSS on JS actually running.
      root.classList.add('scroll-story--live');

      // Choose pinned filmstrip vs stacked fallback.
      this.setMode();
    }

    setMode() {
      const wantPinned = !REDUCE.matches && this.chapters.length > 1 && this.pin && this.stage;
      const next = wantPinned ? 'pinned' : 'stacked';
      if (next === this.mode) return;
      if (this.mode === 'pinned') this.disablePinned();
      else if (this.mode === 'stacked') this.disableStacked();
      this.mode = next;
      if (next === 'pinned') this.enablePinned();
      else this.enableStacked();
    }

    enablePinned() {
      this.root.classList.add('scroll-story--pinned');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.update(); // paint the correct frame immediately (no flash before first scroll)
    }

    disablePinned() {
      this.root.classList.remove('scroll-story--pinned');
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this._ticking = false;
      // Clear the per-frame inline props so the stacked CSS takes over cleanly, and release the
      // inert/aria-hidden gating so a reduced-motion switch mid-session doesn't lock chapters out.
      this.chapters.forEach((chapter) => {
        chapter.style.removeProperty('--ss-x');
        chapter.style.removeProperty('--ss-abs');
        chapter.removeAttribute('inert');
        chapter.removeAttribute('aria-hidden');
      });
    }

    enableStacked() {
      this.chapters.forEach((chapter) => this.observer.observe(chapter));
    }

    disableStacked() {
      this.observer.disconnect();
      this.visible.clear();
    }

    onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      this._rafId = requestAnimationFrame(this.update);
    }

    onResize() {
      this.onScroll();
    }

    // Map scroll progress within the pinned range → continuous scene position, then write each scene's
    // signed distance from centre as custom properties for the CSS filmstrip to consume.
    update() {
      this._ticking = false;
      if (this.mode !== 'pinned' || !this.pin || !this.stage) return;
      const count = this.chapters.length;
      const stageHeight = this.stage.offsetHeight || 1;
      const scrollable = Math.max(this.pin.offsetHeight - stageHeight, 1);
      const top = this.pin.getBoundingClientRect().top;
      const fraction = Math.min(Math.max(-top / scrollable, 0), 1);
      const progress = fraction * (count - 1);

      // Smoothstep the fractional part so each scene dwells then slides (cinematic) while staying
      // monotonic with scroll.
      const base = Math.floor(Math.min(progress, count - 1.0001));
      const frac = progress - base;
      const eased = base + frac * frac * (3 - 2 * frac);

      for (let i = 0; i < count; i += 1) {
        const x = i - eased;
        const chapter = this.chapters[i];
        chapter.style.setProperty('--ss-x', x.toFixed(4));
        chapter.style.setProperty('--ss-abs', Math.abs(x).toFixed(4));
      }

      const activeIndex = Math.min(Math.max(Math.round(progress), 0), count - 1);
      const activeChapter = this.chapters[activeIndex];
      if (activeChapter) this.setActive(activeChapter.dataset.ssId);
    }

    // Scroll so scene `index` is centred (pinned) or into view (stacked).
    scrollToChapter(index) {
      if (this.mode !== 'pinned' || !this.pin || !this.stage) {
        const chapter = this.chapters[index];
        if (chapter) chapter.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      const count = this.chapters.length;
      const stageHeight = this.stage.offsetHeight || 1;
      const scrollable = Math.max(this.pin.offsetHeight - stageHeight, 1);
      const fraction = count > 1 ? index / (count - 1) : 0;
      const target = window.scrollY + this.pin.getBoundingClientRect().top + fraction * scrollable;
      window.scrollTo({ top: target, behavior: REDUCE.matches ? 'auto' : 'smooth' });
    }

    bindDots() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const entry = this.byId.get(dot.dataset.ssId);
          if (!entry) return;
          this.scrollToChapter(entry.index);
        });
      });
    }

    bindToggles() {
      this.byId.forEach((entry, id) => {
        if (!entry.toggle || !entry.video) return;
        entry.toggle.addEventListener('click', () => {
          if (entry.video.paused) {
            this.userPaused.delete(id);
            this.playVideo(entry);
          } else {
            this.userPaused.add(id);
            this.pauseVideo(entry);
          }
        });
      });
    }

    playVideo(entry) {
      const played = entry.video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
      this.reflectToggle(entry, true);
    }

    pauseVideo(entry) {
      entry.video.pause();
      this.reflectToggle(entry, false);
    }

    reflectToggle(entry, playing) {
      if (!entry.toggle) return;
      entry.toggle.classList.toggle('is-playing', playing);
      // Action-named label only (no aria-pressed — a pressed state would contradict the label).
      // Localized strings come from the root data attributes (fall back to English if absent).
      const pause = this.root.dataset.i18nPause || 'Pause background video';
      const play = this.root.dataset.i18nPlay || 'Play background video';
      entry.toggle.setAttribute('aria-label', playing ? pause : play);
    }

    onMotionChange() {
      // Switch pinned⇄stacked to match the new preference…
      this.setMode();
      // …and stop/resume the active chapter's video accordingly.
      this.byId.forEach((entry, id) => {
        if (!entry.video) return;
        const active = id === this.activeId;
        if (REDUCE.matches || !active || this.userPaused.has(id)) this.pauseVideo(entry);
        else this.playVideo(entry);
      });
    }

    onSectionInView(entries) {
      entries.forEach((entry) => {
        this.root.classList.toggle('scroll-story--in-view', entry.isIntersecting);
      });
    }

    // Theme editor: selecting a chapter block brings its chapter into view.
    onBlockSelect(event) {
      const chapter = event.target && event.target.closest
        ? event.target.closest('[data-ss-chapter]')
        : null;
      if (!chapter || !this.root.contains(chapter)) return;
      const index = this.chapters.indexOf(chapter);
      if (index >= 0) this.scrollToChapter(index);
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        const id = entry.target.dataset.ssId;
        if (entry.isIntersecting) this.visible.add(id);
        else this.visible.delete(id);
      });
      if (!this.visible.size) return; // between chapters: keep the last active one lit
      // Choose the topmost still-crossing chapter (smallest index) for a stable, ordered result.
      let nextId = null;
      let best = Infinity;
      this.visible.forEach((id) => {
        const idx = this.byId.get(id)?.index ?? Infinity;
        if (idx < best) {
          best = idx;
          nextId = id;
        }
      });
      this.setActive(nextId);
    }

    setActive(id) {
      if (id === this.activeId) return;
      this.activeId = id;

      this.byId.forEach((entry, entryId) => {
        const on = entryId === id;
        entry.chapter.classList.toggle('is-active', on);
        // In pinned mode the inactive chapters are transformed off-canvas + faded to 0, but stay in
        // the DOM — so gate them out of the tab order and the a11y tree, or keyboard focus lands on an
        // invisible, off-screen control and SRs read every chapter at once. Stacked mode leaves all
        // chapters genuinely visible in flow, so never gate there.
        if (this.mode === 'pinned') {
          entry.chapter.toggleAttribute('inert', !on);
          if (on) entry.chapter.removeAttribute('aria-hidden');
          else entry.chapter.setAttribute('aria-hidden', 'true');
        } else {
          entry.chapter.removeAttribute('inert');
          entry.chapter.removeAttribute('aria-hidden');
        }
        if (entry.dot) {
          entry.dot.classList.toggle('is-active', on);
          if (on) entry.dot.setAttribute('aria-current', 'true');
          else entry.dot.removeAttribute('aria-current');
        }
        if (entry.video) {
          if (on && !REDUCE.matches && !this.userPaused.has(entryId)) this.playVideo(entry);
          else this.pauseVideo(entry);
        }
      });

      if (this.status && id != null) {
        const entry = this.byId.get(id);
        this.status.textContent = `Chapter ${entry.index + 1} of ${this.chapters.length}`;
      }
    }

    destroy() {
      this.observer.disconnect();
      this.inViewObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this.byId.forEach((entry) => {
        if (entry.video) entry.video.pause();
      });
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (REDUCE.removeEventListener) REDUCE.removeEventListener('change', this.onMotionChange);
      else if (REDUCE.removeListener) REDUCE.removeListener(this.onMotionChange); // older Safari
    }
  }

  mountInstances({
    selector: '.scroll-story',
    create: (root) => new ScrollStory(root),
    prop: '__scrollStory',
  });
})();
